import { useMemo } from 'react'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import { Filler, Drawer, Content, SearchBar, Upper, Overlay, Container } from './styles'
import { Link } from 'react-router-dom'
import ProgressCircle from 'components/ProgressCircle'
import { AiOutlineSearch } from 'react-icons/ai'
import LessonDisclosure from '../LessonDisclosure'
import { ILesson } from 'pages/Lesson/hooks/use-get-menu-content/types'
import { useLessonParams } from 'pages/Lesson/hooks/use-lesson-params'
import { CircleLoader, TotalLessonLoader, NameLoader, LessonCardLoader } from './loaders'
import { useDebouncedState } from 'hooks/use-debounced-state'
import Fuse from 'fuse.js'
import FreemiumLockedLesson from '../FreemiumLockedLesson'
import { useLessonContext } from 'pages/Lesson/context'

interface IProps {
  isOpen: boolean
  onToggle(): void
}

export const Menu = (props: IProps) => {
  const { courseId } = useLessonParams()
  const { swrMenuContent } = useLessonContext()

  const { data, isLoading } = swrMenuContent

  const [search, debouncedSetSearch] = useDebouncedState('', 500)

  const filteredLessons = useMemo(() => {
    if (isLoading) return []
    if (!search) return data?.children

    const fuse = new Fuse(data?.children, {
      keys: ['name', 'children.name'],
      threshold: 0.3,
    })

    return fuse.search(search)
  }, [search, data?.children, isLoading])

  const renderLesson = (lessonParam: ILesson | Fuse.FuseResult<ILesson>) => {
    const lesson = 'item' in lessonParam ? lessonParam.item : lessonParam

    const lockType = lesson.available.type

    if (lockType === 'freemium' && data.course.isFreemium && !data.course.isSubscription) {
      return (
        <FreemiumLockedLesson
          lesson={lesson}
          module={data.module}
          course={data.course}
          key={lesson._id}
        />
      )
    }

    return (
      <LessonDisclosure
        key={lesson._id}
        lesson={lesson}
        onChapterClick={() => window.innerWidth < 1000 && props.onToggle()}
      />
    )
  }

  return (
    <Container>
      <Drawer data-is-open={props.isOpen}>
        <button onClick={props.onToggle} className="arrow-button">
          {props.isOpen ? (
            <IoIosArrowBack className="icon" />
          ) : (
            <IoIosArrowForward className="icon" />
          )}
        </button>

        <Content>
          <Upper>
            <div className="group">
              {isLoading ? (
                CircleLoader
              ) : (
                <ProgressCircle
                  percentage={data?.progress}
                  className="circle"
                  svgProps={{ width: 76, height: 76 }}
                />
              )}

              <div className="texts">
                <Link className="link" to={`/course/${courseId}`}>
                  Módulos
                </Link>
                <h2 className="course-name">{isLoading ? NameLoader : data?.module?.name}</h2>
                <p className="total-lessons">
                  {isLoading ? (
                    TotalLessonLoader
                  ) : (
                    <>
                      {data?.completedLessons} de {data?.totalNonEmptyLessons} aulas concluídas
                    </>
                  )}
                </p>
              </div>
            </div>

            <SearchBar>
              <AiOutlineSearch />

              <input
                type="text"
                placeholder="Buscar aula ou capítulo"
                onChange={(e) => debouncedSetSearch(e.target.value)}
              />
            </SearchBar>
          </Upper>

          <div className="lessons">
            {isLoading ? LessonCardLoader : filteredLessons?.map(renderLesson)}
          </div>
        </Content>
      </Drawer>

      <Overlay data-is-visible={props.isOpen} onClick={props.onToggle} />

      <Filler data-is-open={props.isOpen}>
        <div className="line" />
      </Filler>
    </Container>
  )
}
